import React, { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/material';
import { getGridStringOperators } from '@mui/x-data-grid-premium';

import { allSingleSelectOperators } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/CustomFilterOperators/CustomFilterOperators';
import { TASK_WORKLIST } from 'components/pages/patient-source/TaskWorklist/constants';
import {
  assigneeFilterOperators,
  IS_EMPTY,
  IS_NOT_EMPTY
} from 'components/pages/patient-source/TaskWorklist/CustomFilterOperators';
import { resolveTaskPriorityParams } from 'components/root/Container/Layout/Tasks/Task/taskServices';

import { TaskApi } from '../../../../api';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import { defaultDateValueFormatter, parseLocalDate } from '../../../../constants/dateFormat';
import { ROLE_CRA, ROLE_EXTERNAL_AUDITOR } from '../../../../constants/userRoles';
import { userHasRole } from '../../../../services/auth';
import { useActiveTaskUpdate } from '../../../../store/activeTask';
import {
  findTaskStatus,
  OTHER,
  OTHER_TRAVEL_TASK,
  taskPriorities,
  taskPriorityWeights,
  tasksStatuses
} from '../../../root/Container/Layout/Tasks/Task/taskConstants';

import StatusEditCell from './StatusEditCell';

export default function useTableData() {
  const updateActiveTask = useActiveTaskUpdate();
  const [tasks, setTasks] = useState([]);
  const hidePatientColumn = userHasRole([ROLE_CRA, ROLE_EXTERNAL_AUDITOR]);

  const columns = useMemo(
    () => [
      {
        field: 'priority',
        headerName: 'Priority',
        flex: 1,
        minWidth: 100,
        type: 'singleSelect',
        renderCell: ({ row }) => renderPriority(row.priority),
        valueFormatter: value => taskPriorities.find(p => p.id === value)?.name ?? '—',
        valueOptions: taskPriorities.map(p => ({ value: p.id, label: p.name })),
        filterOperators: allSingleSelectOperators,
        sortComparator: (v1, v2) => {
          const val1 = taskPriorityWeights[v1] ?? Number.MAX_SAFE_INTEGER;
          const val2 = taskPriorityWeights[v2] ?? Number.MAX_SAFE_INTEGER;

          return val1 - val2;
        }
      },
      ...(!hidePatientColumn
        ? [
            {
              field: 'patientName',
              headerName: 'Patient',
              flex: 1,
              minWidth: 150,
              renderCell: ({ row }) => resolveCellValue(row.patientName),
              filterOperators: getGridStringOperators().filter(op => op.value === 'contains')
            }
          ]
        : []),
      {
        field: 'sequenceNumber',
        headerName: 'ID',
        flex: 1,
        minWidth: 100,
        renderCell: ({ row }) => resolveCellValue(row.sequenceNumber)
      },
      {
        field: 'subjectId',
        headerName: 'Subject ID',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => resolveCellValue(row.subjectId || 'N/A')
      },
      {
        field: 'topic',
        headerName: 'Topic',
        flex: 1,
        minWidth: 150,
        valueGetter: (_, row) => topicContents(row),
        renderCell: ({ row }) => resolveCellValue(topicContents(row)),
        filterOperators: getGridStringOperators().filter(op => op.value !== IS_EMPTY && op.value !== IS_NOT_EMPTY)
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => resolveCellValue(row.description)
      },
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => resolveCellValue(row.studyName),
        filterOperators: getGridStringOperators().filter(op => op.value !== IS_EMPTY && op.value !== IS_NOT_EMPTY)
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => resolveCellValue(row.siteName),
        filterOperators: getGridStringOperators().filter(op => op.value !== IS_EMPTY && op.value !== IS_NOT_EMPTY)
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => resolveCellValue(row.encounterName)
      },
      {
        field: 'assignee',
        headerName: 'Assignee',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => resolveCellValue(row.assignee === '-- --' ? null : row.assignee),
        filterOperators: assigneeFilterOperators
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueGetter: parseLocalDate,
        valueFormatter: defaultDateValueFormatter,
        renderCell: ({ row }) => defaultDateValueFormatter(parseLocalDate(row.dueDate))
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 150,
        editable: true,
        type: 'singleSelect',
        valueOptions: tasksStatuses.map(s => ({ value: s.id, label: s.name })),
        filterOperators: allSingleSelectOperators,
        valueFormatter: value => findTaskStatus(value)?.name ?? '—',
        renderCell: ({ row }) => {
          const label = findTaskStatus(row.status)?.name ?? row.status;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
              <Box component="span" sx={{ flex: 1 }}>
                {label || '—'}
              </Box>
              <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'action.active' }} />
            </Box>
          );
        },
        renderEditCell: params => <StatusEditCell {...params} />
      }
    ],
    [hidePatientColumn]
  );

  async function processRowUpdate(updatedRow, originalRow) {
    if (updatedRow.status === originalRow.status) return Promise.resolve(updatedRow);

    try {
      const { data: updatedTask } = await TaskApi.updateStatus(
        updatedRow.id,
        updatedRow.studySiteId,
        updatedRow.status
      );
      NotificationManager.success(`Task ${updatedTask?.sequenceNumber} updated`);
      updateActiveTask({ status: updatedTask.status });
      const resultRow = { ...updatedRow, status: updatedTask.status };
      setTasks(prev => prev.map(t => (t.id === resultRow.id ? resultRow : t)));
      return resultRow;
    } catch {
      NotificationManager.error(`Failed to update task ${updatedRow?.sequenceNumber}`);
      return originalRow;
    }
  }

  return {
    tasks,
    setTasks,
    columns,
    processRowUpdate,
    tableVersion: '1.01',
    tableName: TASK_WORKLIST
  };
}

function topicContents(row) {
  const { topic, topicName, topicTitle } = row;
  switch (topic) {
    case OTHER:
      return `${topicName}: ${topicTitle}`;
    case OTHER_TRAVEL_TASK:
      return topicTitle;
    default:
      return topicName;
  }
}

function resolveCellValue(value) {
  return value ? <span>{value}</span> : '—';
}

function renderPriority(priority) {
  const priorityParams = resolveTaskPriorityParams(priority);
  if (priorityParams) {
    const { backgroundColor, TaskIcon, color, label } = priorityParams;
    return (
      <div className="priority-cell" style={{ backgroundColor: backgroundColor }}>
        <TaskIcon sx={{ color: color }} />
        <span className="etc-priority-label" style={{ color: color }}>
          {label}
        </span>
      </div>
    );
  } else {
    return '—';
  }
}
